import { useCallback, useSyncExternalStore } from "react";

/** Evento do Chrome/Edge/Samsung Internet que permite abrir a instalação pelo app (não existe no iOS). */
type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let captured = false;
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

/**
 * Guarda o `beforeinstallprompt` (que pode chegar antes de o React montar) para o "Instalar app" do "Mais".
 * Chamado no `main.tsx`, antes do `createRoot`.
 */
export function captureInstallPrompt() {
  if (captured) return;
  captured = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    installed = true;
    notify();
  });
}

/** Só para testes: esquece o evento guardado e a instalação. */
export function resetInstallForTests() {
  deferred = null;
  installed = false;
  notify();
}

const STANDALONE = "(display-mode: standalone)";

/** Aberto pelo ícone (app instalado): `display-mode: standalone` ou, no iOS, `navigator.standalone`. */
export function isStandalone() {
  return (
    window.matchMedia(STANDALONE).matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export type InstallPlatform = "ios" | "in-app" | "other";

/**
 * - `in-app`: navegador dentro de outro app (Instagram, Facebook, Gmail, LinkedIn), que não instala;
 * - `ios`: iPhone, iPod ou iPad, inclusive o iPadOS que se apresenta como Mac (mas tem toque);
 * - `other`: o resto (Android, desktop).
 */
export function platform(
  ua = navigator.userAgent,
  touchPoints = navigator.maxTouchPoints ?? 0,
): InstallPlatform {
  if (/Instagram|FBAN|FBAV|FB_IAB|FBIOS|LinkedInApp|GSA\/|Gmail/i.test(ua)) return "in-app";
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Macintosh/.test(ua) && touchPoints > 1) return "ios";
  return "other";
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  const mql = window.matchMedia(STANDALONE);
  mql.addEventListener("change", onChange);
  return () => {
    listeners.delete(onChange);
    mql.removeEventListener("change", onChange);
  };
}

const snapshot = () => `${deferred ? 1 : 0}${installed ? 1 : 0}${isStandalone() ? 1 : 0}`;

/**
 * Estado da instalação para o "Mais". `available`: a opção aparece (fora do modo instalado e com evento guardado,
 * no iOS, ou num navegador de outro app). `prompt()` abre a instalação do navegador quando há evento.
 */
export function useInstall() {
  const state = useSyncExternalStore(subscribe, snapshot, () => "000");
  const canPrompt = state[0] === "1";
  const standalone = state[2] === "1";
  const where = platform();
  const available = !standalone && state[1] !== "1" && (canPrompt || where !== "other");

  const prompt = useCallback(async () => {
    const event = deferred;
    if (!event) return;
    deferred = null; // o evento só pode ser usado uma vez
    notify();
    await event.prompt();
    await event.userChoice.catch(() => undefined);
  }, []);

  return { available, canPrompt, standalone, platform: where, prompt };
}
